<script>
    // Address validation modal — fires when the oxid-proxy backend tags the
    // saved billing/shipping address with one of the non-OK AMS statuses
    // ('address_needs_correction', 'address_multiple_variants', 'address_not_found').
    //
    // The backend never blocks the saveCheckoutUser mutation on validation
    // outcomes (soft-fail), so this modal is the single point where the user
    // decides what to do with the suggestion. Buttons:
    //
    //   - "Original übernehmen"  → record "user-accepted-original" client-side
    //                              and continue. Status stays in DB; backend
    //                              treats this as informational only.
    //   - "Vorschlag übernehmen" → write the parsed Google suggestion back
    //                              into the local Address state. The caller is
    //                              then expected to re-run saveUser, which
    //                              will produce a new hash and (hopefully)
    //                              flip the status to 'address_correct'.
    //   - "Adresse bearbeiten"   → close + caller switches addressMode so the
    //                              user can hand-edit. No store mutation.
    //
    // For 'address_not_found' (or whenever predictions are missing) we drop
    // the "Vorschlag übernehmen" button — there's nothing to adopt.

    import {t} from "$utils/lang.js";
    import {predictionToAddress, parsePredictions, differsFromOriginal} from "$lib/addressValidation.js";

    let {
        open = $bindable(false),
        // Original address (the one the user typed). Display only — we don't
        // mutate it from inside the modal.
        original = null,
        // Raw amsPredictions JSON string OR an already-parsed predictions object.
        // We accept both so callers can pre-parse if they want.
        predictions = null,
        // 'address_needs_correction' | 'address_multiple_variants' | 'address_not_found'
        status = 'address_needs_correction',
        // Caller hook. action ∈ 'use-original' | 'use-prediction' | 'edit'.
        // When 'use-prediction' fires we also pass the suggested address fields
        // so the caller doesn't have to re-derive them.
        onResolve = (_action, _suggestion) => {},
    } = $props();

    let dialog;

    // Sync `open` ↔ <dialog>.
    $effect(() => {
        if (!dialog) return;
        if (open && !dialog.open) dialog.showModal();
        else if (!open && dialog.open) dialog.close();
    });

    // Always treat predictions as parsed shape — accept either raw JSON or object.
    const parsed = $derived.by(() => {
        if (!predictions) return null;
        if (typeof predictions === 'string') return parsePredictions(predictions);
        return predictions;
    });

    const suggestion = $derived(predictionToAddress(parsed));

    // 'not_found' has no usable suggestion. We also defensively fall through
    // to "no suggestion" if predictionToAddress() failed (e.g. missing
    // postalAddress in the response).
    // `hasSuggestion` distinguishes "we have something to offer" from "no
    // suggestion at all". A suggestion identical to the original is NOT
    // usable (Google echoed the input back without normalising anything —
    // common when an unconfirmed component triggers the modal but Google
    // can't propose an alternative). Treating it as no-suggestion hides
    // the duplicate Vorschlag card and the "Vorschlag übernehmen" button.
    const hasSuggestion = $derived(
        status !== 'address_not_found'
        && suggestion
        && (suggestion.street || suggestion.streetNo || suggestion.zip || suggestion.city)
        && differsFromOriginal(original, suggestion)
    );

    const heading = $derived.by(() => {
        switch (status) {
            case 'address_multiple_variants':
                return t('Mehrere mögliche Adressen gefunden');
            case 'address_not_found':
                return t('Diese Adresse konnte nicht gefunden werden');
            case 'address_needs_correction':
            default:
                return t('Deine Adresse muss korrigiert werden');
        }
    });

    const explainer = $derived.by(() => {
        // When no usable suggestion is available (none at all, or
        // identical to the original), don't promise one in the copy.
        // Backend can return needs_correction without a Google
        // postalAddress (local packstation validator) or with a
        // suggestion that echoes the input (Google flagged an
        // unconfirmed component but had nothing better to offer).
        if (!hasSuggestion) {
            return t('Wir konnten deine Adresse nicht zweifelsfrei prüfen. Bitte überprüfe sie oder fahre mit deiner Eingabe fort.');
        }
        switch (status) {
            case 'address_multiple_variants':
                return t('Wir haben mehrere mögliche Treffer. Bitte prüfe den Vorschlag.');
            case 'address_not_found':
                return t('Wir konnten deine Adresse nicht zweifelsfrei finden. Du kannst trotzdem mit deiner Eingabe fortfahren oder sie bearbeiten.');
            case 'address_needs_correction':
            default:
                return t('Wir empfehlen den unten gezeigten Vorschlag, damit deine Lieferung sicher ankommt.');
        }
    });

    function close(action) {
        const payload = action === 'use-prediction' ? suggestion : null;
        open = false;
        onResolve(action, payload);
    }

    // ESC / backdrop dismiss → treat as "edit" so the user lands in a state
    // where they can fix things (not silently accepted).
    function handleClose() {
        if (open) {
            open = false;
            onResolve('edit', null);
        }
    }
    function handleBackdropClick(e) {
        if (e.target === dialog) handleClose();
    }

    // For the Vorschlag card we render a merged view: fields Google left
    // empty (because they were UNCONFIRMED) fall back to the user's
    // original input so the card shows a complete address. The raw
    // `suggestion` object stays sparse — its empty fields are the signal
    // applyPredictionAndResave uses to keep the original value on adopt.
    const mergedSuggestion = $derived.by(() => {
        if (!suggestion) return null;
        const orig = original || {};
        return {
            street: suggestion.street || orig.street || '',
            streetNo: suggestion.streetNo || orig.streetNo || '',
            zip: suggestion.zip || orig.zip || '',
            city: suggestion.city || orig.city || '',
            addInfo: suggestion.addInfo || orig.addInfo || '',
        };
    });

    // Helper to format an address block for compact rendering. Keeps the
    // suggestion and the original visually parallel.
    function formatLine1(addr) {
        if (!addr) return '';
        const street = (addr.street || '').trim();
        const no = (addr.streetNo || '').trim();
        return [street, no].filter(Boolean).join(' ');
    }
    function formatLine2(addr) {
        if (!addr) return '';
        const zip = (addr.zip || '').trim();
        const city = (addr.city || '').trim();
        return [zip, city].filter(Boolean).join(' ');
    }
</script>

<dialog
    bind:this={dialog}
    onclose={handleClose}
    onclick={handleBackdropClick}
    data-checkout-modal="address-validation"
    data-checkout-modal-status={status}
    class="m-auto rounded-lg p-0 shadow-2xl backdrop:bg-black/50 backdrop:backdrop-blur-sm w-[560px] max-w-[95vw]"
>
    <div class="bg-white p-5 flex flex-col gap-4">
        <h3 class="text-base font-bold text-cyprus" data-checkout-modal-heading>
            {heading}
        </h3>

        <p class="text-sm text-gray-700">{explainer}</p>

        <div class="flex flex-col gap-3 sm:flex-row sm:gap-4">
            <!-- Original (user's input) -->
            <div
                class="flex-1 border border-grey rounded-md p-3 text-sm"
                data-checkout-address-variant="original"
            >
                <div class="font-semibold text-cyprus mb-1">
                    {t('Deine Eingabe')}
                </div>
                {#if original}
                    <div>{formatLine1(original)}</div>
                    <div>{formatLine2(original)}</div>
                    {#if original.addInfo}
                        <div class="text-gray-600">{original.addInfo}</div>
                    {/if}
                {:else}
                    <div class="text-gray-500">—</div>
                {/if}
            </div>

            {#if hasSuggestion}
                <!-- Google suggestion (merged: empty fields fall back to
                     the user's original so the card always shows a
                     complete address). -->
                <div
                    class="flex-1 border-2 border-cyprus rounded-md p-3 text-sm bg-cyprus/5"
                    data-checkout-address-variant="suggestion"
                >
                    <div class="font-semibold text-cyprus mb-1">
                        {t('Vorschlag')}
                    </div>
                    <div>{formatLine1(mergedSuggestion)}</div>
                    <div>{formatLine2(mergedSuggestion)}</div>
                    {#if mergedSuggestion.addInfo}
                        <div class="text-gray-600">{mergedSuggestion.addInfo}</div>
                    {/if}
                </div>
            {/if}
        </div>

        <div class="flex flex-col gap-2 pt-2 sm:flex-row sm:flex-wrap sm:justify-end">
            <button
                type="button"
                onclick={() => close('edit')}
                data-checkout-action="edit-address"
                class="px-4 py-2 rounded-md font-semibold text-sm border border-grey hover:bg-gray-50 transition-colors cursor-pointer"
            >
                {t('Adresse bearbeiten')}
            </button>
            <button
                type="button"
                onclick={() => close('use-original')}
                data-checkout-action="use-original"
                class="px-4 py-2 rounded-md font-semibold text-sm border border-grey hover:bg-gray-50 transition-colors cursor-pointer"
            >
                {t('Original übernehmen')}
            </button>
            {#if hasSuggestion}
                <button
                    type="button"
                    onclick={() => close('use-prediction')}
                    data-checkout-action="use-prediction"
                    class="px-4 py-2 rounded-md font-semibold text-sm bg-cyprus hover:bg-[#003055] text-white transition-colors cursor-pointer"
                >
                    {t('Vorschlag übernehmen')}
                </button>
            {/if}
        </div>
    </div>
</dialog>
